import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Check, X, MessageCircle, ArrowUpRight, ArrowLeft, Compass } from "lucide-react";
import { api, WHATSAPP_LINK } from "../lib/config";
import { Reveal, SplitLines } from "../components/Motion";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import Seo from "../components/Seo";

export default function TripDetail() {
  const { id } = useParams();
  const [trip, setTrip] = useState(null);
  const [bookOpen, setBookOpen] = useState(false);

  useEffect(() => {
    api.get(`/trips/${id}`).then((r) => setTrip(r.data)).catch(() => setTrip(false));
  }, [id]);

  if (trip === null) return <div className="pt-32 text-center font-mono text-sm text-owt-muted">Loading...</div>;
  if (trip === false) return <div className="pt-32 text-center"><Link to="/trips" className="underline">Trip not found — back to all trips</Link></div>;

  const waMsg = `Hi Once Way, I'd like to book the ${trip.name} trip. Please share details.`;

  const tripJsonLd = {
    "@context": "https://schema.org",
    "@type": "TouristTrip",
    "name": trip.name,
    "description": trip.short_description || trip.description,
    "image": trip.hero_image,
    "touristType": "Small group",
    ...(trip.price > 0 && {
      offers: {
        "@type": "Offer",
        price: trip.price,
        priceCurrency: trip.currency || "INR",
        availability: trip.seats_remaining > 0 ? "https://schema.org/InStock" : "https://schema.org/SoldOut",
      },
    }),
  };

  return (
    <div className="pt-24 md:pt-28" data-testid={`trip-detail-${trip.id}`}>
      <Seo
        title={trip.name}
        description={trip.short_description || trip.description?.slice(0, 160)}
        path={`/trips/${trip.id}`}
        image={trip.hero_image}
        type="article"
        jsonLd={tripJsonLd}
      />
      {/* HERO */}
      <section className="relative h-[80svh] min-h-[520px] overflow-hidden">
        <img src={trip.hero_image} alt={trip.name} className="absolute inset-0 w-full h-full object-cover"/>
        <div className="absolute inset-0 bg-gradient-to-t from-owt-ink/80 via-owt-ink/20 to-transparent"/>
        <div className="relative z-10 h-full max-w-[1600px] mx-auto px-5 md:px-10 flex flex-col justify-end pb-12 md:pb-16 text-owt-surface">
          <Link to="/trips" className="font-mono text-[11px] tracking-[0.25em] uppercase inline-flex items-center gap-2 mb-6 hover:text-owt-terracotta" data-testid="back-to-trips"><ArrowLeft className="w-4 h-4"/> All Trips</Link>
          <div className="font-mono text-[11px] tracking-[0.3em] uppercase opacity-80">{trip.country} · {trip.duration_days} Days · {trip.difficulty}</div>
          <h1 className="font-display text-6xl md:text-8xl leading-[0.9] tracking-tighter mt-3">
            <SplitLines text={trip.name}/>
          </h1>
          <p className="max-w-2xl mt-4 opacity-90">{trip.short_description}</p>
        </div>
      </section>

      {/* CONTENT */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Sticky sidebar */}
          <aside className="lg:col-span-4 lg:sticky lg:top-28 self-start" data-testid="trip-sticky-panel">
            <div className="boarding-pass p-6 md:p-8">
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-owt-muted mb-4">Boarding Pass · OWT</div>
              {trip.price > 0 ? (
                <div className="flex items-baseline gap-3 flex-wrap">
                  {trip.original_price && trip.original_price > trip.price && (
                    <span className="font-display text-xl text-owt-muted line-through decoration-owt-terracotta/70">₹{Number(trip.original_price).toLocaleString("en-IN")}</span>
                  )}
                  <span className="font-display text-3xl text-owt-forest leading-tight">₹{Number(trip.price).toLocaleString("en-IN")}</span>
                </div>
              ) : (
                <div className="font-display text-3xl text-owt-forest leading-tight">Coming soon</div>
              )}
              <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-owt-muted mt-1">{trip.price > 0 ? "per traveller · all-in (ex. flights)" : "message us to reserve"}</div>

              <div className="dashed-line my-5"/>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="font-mono text-[9px] tracking-[0.2em] uppercase text-owt-muted mb-1">Depart</div>
                  <div className="font-display text-owt-forest">{new Date(trip.start_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}</div>
                </div>
                <div>
                  <div className="font-mono text-[9px] tracking-[0.2em] uppercase text-owt-muted mb-1">Return</div>
                  <div className="font-display text-owt-forest">{new Date(trip.end_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}</div>
                </div>
                <div>
                  <div className="font-mono text-[9px] tracking-[0.2em] uppercase text-owt-muted mb-1">Seats left</div>
                  <div className="font-display text-owt-terracotta">{trip.seats_remaining} / {trip.seats_total}</div>
                </div>
                <div>
                  <div className="font-mono text-[9px] tracking-[0.2em] uppercase text-owt-muted mb-1">Level</div>
                  <div className="font-display text-owt-forest">{trip.difficulty}</div>
                </div>
              </div>

              <div className="dashed-line my-5"/>
              <button
                onClick={() => setBookOpen(true)}
                data-testid="book-now"
                className="w-full bg-owt-forest text-owt-surface py-3 font-mono text-[11px] tracking-[0.25em] uppercase hover:bg-owt-terracotta transition-colors inline-flex items-center justify-center gap-2"
              >
                Book Now <ArrowUpRight className="w-4 h-4"/>
              </button>
              <a
                href={WHATSAPP_LINK(waMsg)}
                target="_blank" rel="noreferrer"
                data-testid="whatsapp-trip"
                className="mt-3 w-full bg-[#25D366] text-white py-3 font-mono text-[11px] tracking-[0.25em] uppercase hover:opacity-90 transition-opacity inline-flex items-center justify-center gap-2"
              >
                <MessageCircle className="w-4 h-4"/> WhatsApp Us to Book
              </a>
            </div>
          </aside>

          {/* Main scrolling content */}
          <div className="lg:col-span-8 space-y-14">
            <Reveal>
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-owt-muted mb-3">· About the trip</div>
              <p className="font-display text-2xl md:text-3xl text-owt-forest leading-tight">{trip.description}</p>
            </Reveal>

            <Reveal>
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-owt-muted mb-4">· Day by day</div>
              <div className="mb-6 border-l-2 border-owt-terracotta pl-4 py-2 bg-owt-sand/40">
                <p className="text-sm text-owt-ink/80 leading-relaxed">
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-owt-terracotta mr-2">Please note</span>
                  The itinerary below is indicative and may change based on weather, road conditions, wildlife movement and other factors on the ground.
                </p>
              </div>
              <Accordion type="single" collapsible className="border-t border-owt-border">
                {trip.itinerary.map((d) => (
                  <AccordionItem key={d.day} value={`d${d.day}`} className="border-b border-owt-border">
                    <AccordionTrigger className="font-display text-2xl text-owt-forest hover:no-underline py-5" data-testid={`day-${d.day}`}>
                      <span className="flex items-baseline gap-4"><span className="font-mono text-sm text-owt-terracotta">Day {String(d.day).padStart(2, "0")}</span> {d.title}</span>
                    </AccordionTrigger>
                    <AccordionContent className="pb-6">
                      <p className="text-owt-muted leading-relaxed">{d.description}</p>
                      {Array.isArray(d.images) && d.images.length > 0 && (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-5">
                          {d.images.map((src, i) => (
                            <div key={i} className="aspect-square overflow-hidden bg-owt-sand">
                              <motion.img src={src} alt={`Day ${d.day}`} className="w-full h-full object-cover" whileHover={{ scale: 1.05 }} transition={{ duration: 1 }}/>
                            </div>
                          ))}
                        </div>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>

            {/* Inclusions / Exclusions */}
            {(trip.inclusions.length > 0 || trip.exclusions.length > 0) && (
            <Reveal>
              <div data-testid="inclusions-exclusions" className="relative">
                <div className="flex items-center gap-3 mb-6">
                  <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-owt-terracotta">The fine print</div>
                  <div className="flex-1 border-t border-dashed border-owt-border"/>
                  <Compass className="w-4 h-4 text-owt-terracotta compass-spin" strokeWidth={1.2}/>
                </div>

                <div className="grid md:grid-cols-2 gap-4 md:gap-6">
                  {/* Included */}
                  <div className="border border-owt-border bg-owt-surface p-6 md:p-8 relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-1 bg-owt-forest"/>
                    <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-owt-forest mb-5">✓ Included</div>
                    <ul className="space-y-0">
                      {trip.inclusions.map((i, idx) => (
                        <motion.li
                          key={idx}
                          initial={{ opacity: 0, x: -12 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true, margin: "-40px" }}
                          transition={{ delay: idx * 0.03, duration: 0.5 }}
                          className="flex items-start gap-3 py-2.5 border-b border-dashed border-owt-border last:border-b-0"
                        >
                          <Check className="w-4 h-4 mt-1 shrink-0 text-owt-forest"/>
                          <span className="text-owt-ink text-[0.95rem] leading-snug">{i}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>

                  {/* Excluded */}
                  <div className="border border-owt-border bg-owt-sand/40 p-6 md:p-8 relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-1 bg-owt-terracotta"/>
                    <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-owt-terracotta mb-5">✕ Not included</div>
                    <ul className="space-y-0">
                      {trip.exclusions.map((i, idx) => (
                        <motion.li
                          key={idx}
                          initial={{ opacity: 0, x: 12 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true, margin: "-40px" }}
                          transition={{ delay: idx * 0.03, duration: 0.5 }}
                          className="flex items-start gap-3 py-2.5 border-b border-dashed border-owt-border last:border-b-0"
                        >
                          <X className="w-4 h-4 mt-1 shrink-0 text-owt-terracotta"/>
                          <span className="text-owt-muted text-[0.95rem] leading-snug">{i}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Early-bird note */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7 }}
                  className="mt-6 border border-owt-forest bg-owt-forest text-owt-surface p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4 justify-between"
                >
                  <div className="flex items-center gap-3">
                    <div className="stamp bg-owt-forest shrink-0 !border-owt-sand !text-owt-sand">Early Bird</div>
                    <p className="font-display text-lg md:text-xl leading-tight">
                      Early-bird discount for the <span className="italic text-owt-sand">first 4 travellers.</span> WhatsApp us for the current rate.
                    </p>
                  </div>
                  <a
                    href={WHATSAPP_LINK(waMsg)}
                    target="_blank" rel="noreferrer"
                    data-testid="whatsapp-details"
                    className="shrink-0 bg-[#25D366] text-white px-5 py-3 font-mono text-[11px] tracking-[0.25em] uppercase hover:opacity-90 inline-flex items-center gap-2 w-fit"
                  >
                    <MessageCircle className="w-4 h-4"/> WhatsApp Us
                  </a>
                </motion.div>
              </div>
            </Reveal>
            )}

            {/* Gallery */}
            {trip.gallery.length > 0 && (
              <Reveal>
                <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-owt-muted mb-4">· Gallery</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {trip.gallery.map((g, i) => (
                    <div key={i} className={`overflow-hidden bg-owt-sand ${i === 0 ? "col-span-2 aspect-[4/3]" : "aspect-square"}`}>
                      <motion.img src={g} alt="" className="w-full h-full object-cover" whileHover={{ scale: 1.05 }} transition={{ duration: 1 }}/>
                    </div>
                  ))}
                </div>
              </Reveal>
            )}

            {/* FAQ */}
            {trip.faqs.length > 0 && (
              <Reveal>
                <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-owt-muted mb-4">· FAQ</div>
                <Accordion type="single" collapsible className="border-t border-owt-border">
                  {trip.faqs.map((f, i) => (
                    <AccordionItem key={i} value={`f${i}`} className="border-b border-owt-border">
                      <AccordionTrigger className="font-display text-xl text-owt-forest hover:no-underline">{f.q}</AccordionTrigger>
                      <AccordionContent className="text-owt-muted">{f.a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Reveal>
            )}

            {/* CTA row */}
            <Reveal>
              <div className="border border-owt-border p-8 md:p-12 bg-owt-surface flex flex-col md:flex-row md:items-center gap-6 justify-between">
                <div>
                  <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-owt-muted mb-2">Ready to travel?</div>
                  <h3 className="font-display text-3xl md:text-4xl text-owt-forest">Reserve your seat on this trip.</h3>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button onClick={() => setBookOpen(true)} data-testid="cta-book-now" className="bg-owt-forest text-owt-surface px-6 py-3 font-mono text-[11px] tracking-[0.25em] uppercase hover:bg-owt-terracotta transition-colors">Book Now</button>
                  <a href={WHATSAPP_LINK(waMsg)} target="_blank" rel="noreferrer" data-testid="cta-whatsapp" className="bg-[#25D366] text-white px-6 py-3 font-mono text-[11px] tracking-[0.25em] uppercase hover:opacity-90 inline-flex items-center gap-2"><MessageCircle className="w-4 h-4"/> WhatsApp Us to Book</a>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <BookingModal open={bookOpen} onOpenChange={setBookOpen} trip={trip}/>
    </div>
  );
}

const AGE_GROUPS = ["Under 25", "25–34", "35–44", "45–54", "55+"];
const FLIGHT_OPTS = ["Yes, already booked", "Not yet, I'll book myself", "Need help booking"];

function BookingModal({ open, onOpenChange, trip }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", travellers: 1, age_group: "25–34", flights_booked: "Not yet, I'll book myself", notes: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/enquiries/booking", { ...form, trip_id: trip.id, trip_name: trip.name });
      setDone(true);
      toast.success("Booking request sent!");
    } catch { toast.error("Try again please."); }
    finally { setLoading(false); }
  };
  const close = () => { onOpenChange(false); setTimeout(() => { setDone(false); setForm({ name: "", email: "", phone: "", travellers: 1, age_group: "25–34", flights_booked: "Not yet, I'll book myself", notes: "" }); }, 400); };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); else onOpenChange(true); }}>
      <DialogContent className="max-w-xl bg-owt-surface border-owt-border max-h-[90vh] overflow-y-auto" data-testid="booking-modal">
        {done ? (
          <div className="py-10 text-center" data-testid="booking-thank-you">
            <div className="mx-auto w-16 h-16 rounded-full border-2 border-owt-terracotta flex items-center justify-center">
              <Check className="w-8 h-8 text-owt-terracotta"/>
            </div>
            <p className="mt-6 font-mono text-[10px] tracking-[0.3em] uppercase text-owt-muted">Booking Submitted</p>
            <h2 className="mt-3 font-display text-4xl text-owt-forest leading-tight">Thank you!</h2>
            <p className="mt-4 text-owt-muted max-w-sm mx-auto leading-relaxed">
              We've received your booking request for <span className="text-owt-forest">{trip.name}</span>. We'll get in touch with you soon over email and WhatsApp with the next steps.
            </p>
            <button onClick={close} className="mt-8 bg-owt-forest text-owt-surface px-6 py-3 font-mono text-[11px] tracking-[0.25em] uppercase hover:bg-owt-terracotta transition-colors" data-testid="thank-you-close">Close</button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-3xl text-owt-forest">Reserve your seat</DialogTitle>
              <DialogDescription className="text-owt-muted">
                {trip.name} · {trip.price > 0 ? `₹${Number(trip.price).toLocaleString("en-IN")} per traveller` : "Price on request"}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 pt-2">
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-owt-muted block mb-1">Full name</label>
                  <Input required value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} data-testid="book-name"/>
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-owt-muted block mb-1">Email</label>
                  <Input required type="email" value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} data-testid="book-email"/>
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-owt-muted block mb-1">Phone (with country code)</label>
                  <Input required placeholder="+91 86603 38714" value={form.phone} onChange={(e)=>setForm({...form, phone: e.target.value})} data-testid="book-phone"/>
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-owt-muted block mb-1">Number of people</label>
                  <Input required type="number" min="1" value={form.travellers} onChange={(e)=>setForm({...form, travellers: Number(e.target.value)})} data-testid="book-travellers"/>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-owt-muted block mb-1">Age group</label>
                  <Select value={form.age_group} onValueChange={(v)=>setForm({...form, age_group: v})}>
                    <SelectTrigger data-testid="book-age"><SelectValue/></SelectTrigger>
                    <SelectContent>{AGE_GROUPS.map((a)=><SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-owt-muted block mb-1">Flights</label>
                  <Select value={form.flights_booked} onValueChange={(v)=>setForm({...form, flights_booked: v})}>
                    <SelectTrigger data-testid="book-flights"><SelectValue/></SelectTrigger>
                    <SelectContent>{FLIGHT_OPTS.map((a)=><SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-owt-muted block mb-1">Any other details</label>
                <Textarea placeholder="Dietary needs, medical conditions, room preferences…" rows={3} value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})} data-testid="book-notes"/>
              </div>
              <button disabled={loading} type="submit" data-testid="book-submit" className="w-full bg-owt-forest text-owt-surface py-3 font-mono text-[11px] tracking-[0.25em] uppercase hover:bg-owt-terracotta transition-colors disabled:opacity-60">
                {loading ? "Sending..." : "Submit Booking Request"}
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
